/**
 * WebsitesPage — lists all websites the current user has access to, grouped by org.
 * Each website is displayed as a dashboard card with score, status, and issue counts.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useOrgs } from "../hooks/use-orgs.js";
import { useWebsites } from "../hooks/use-websites.js";
import { Skeleton } from "../components/skeleton.js";
import { getLatestScore } from "../api/score.js";
import { getLatestReport } from "../api/report.js";
import type { Organisation, Website, ScanFrequency } from "../api/types.js";
import type { ScoreBand } from "../api/product-types.js";

// ── Helpers ──────────────────────────────────────────────────────────────────

const SCAN_FREQUENCY_LABELS: Record<ScanFrequency, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  manual: "Manual",
};

/**
 * Return a human-readable relative time string for a given ISO date.
 * Keeps it simple: minutes, hours, days, weeks, months.
 */
function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;

  if (diffMs < 0) return "just now";

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;

  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

// ── WebsiteCard — a single card in the grid ─────────────────────────────────

function WebsiteCard({ site }: { site: Website }) {
  const neverScanned = site.lastScanAt == null;

  // Determine status badge
  let statusLabel: string;
  let statusClass: string;
  if (neverScanned) {
    statusLabel = "Never scanned";
    statusClass = "website-card__badge--never";
  } else if (site.verified) {
    statusLabel = "Verified";
    statusClass = "website-card__badge--verified";
  } else {
    statusLabel = "Unverified";
    statusClass = "website-card__badge--unverified";
  }

  return (
    <Link to={`/websites/${site.id}`} className="website-card">
      <div className="website-card__header">
        <div>
          <div className="website-card__domain">{site.domain}</div>
        </div>

        {/* Score is rendered conditionally: skip if never scanned */}
        {!neverScanned && <WebsiteScoreBadge websiteId={site.id} />}
        {neverScanned && (
          <span className="website-card__score website-card__score--none">
            --
          </span>
        )}
      </div>

      <div className="website-card__meta">
        <span className={`website-card__badge ${statusClass}`}>
          {statusLabel}
        </span>
      </div>

      {/* Issue counts — only fetch if the site has been scanned */}
      {!neverScanned && <WebsiteIssueBadges websiteId={site.id} />}

      <div className="website-card__footer">
        <span>
          {neverScanned
            ? "Never scanned"
            : `Scanned ${timeAgo(site.lastScanAt!)}`}
        </span>
        <span>{SCAN_FREQUENCY_LABELS[site.scanFrequency] ?? site.scanFrequency}</span>
      </div>
    </Link>
  );
}

// ── Sub-components that use React Query per-card ────────────────────────────

function WebsiteScoreBadge({ websiteId }: { websiteId: string }) {
  const { data: score, isLoading } = useQuery({
    queryKey: ["website-card-score", websiteId],
    queryFn: () => getLatestScore(websiteId),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <span className="website-card__score website-card__score--none">...</span>
    );
  }

  const band: ScoreBand | "none" = score?.scoreBand ?? "none";
  const scoreValue = score?.scoreValue;

  if (scoreValue == null) {
    return (
      <span className="website-card__score website-card__score--none">--</span>
    );
  }

  return (
    <span className={`website-card__score website-card__score--${band}`}>
      {scoreValue}
    </span>
  );
}

function WebsiteIssueBadges({ websiteId }: { websiteId: string }) {
  const { data: score } = useQuery({
    queryKey: ["website-card-score", websiteId],
    queryFn: () => getLatestScore(websiteId),
    staleTime: 60_000,
  });

  const { data: report } = useQuery({
    queryKey: ["website-card-report", websiteId],
    queryFn: () => getLatestReport(websiteId),
    staleTime: 60_000,
  });

  // Prefer report counts, fall back to score counts
  const critical = report?.criticalCount ?? score?.criticalCount ?? 0;
  const serious = report?.seriousCount ?? score?.seriousCount ?? 0;

  if (critical === 0 && serious === 0) return null;

  return (
    <div className="website-card__issues">
      {critical > 0 && (
        <span className="website-card__issue-count website-card__issue-count--critical">
          {critical} critical
        </span>
      )}
      {serious > 0 && (
        <span className="website-card__issue-count website-card__issue-count--serious">
          {serious} serious
        </span>
      )}
    </div>
  );
}

// ── OrgWebsiteGroup — one group per organisation ────────────────────────────

function OrgWebsiteGroup({
  org,
  search,
  onMatchCount,
}: {
  org: Organisation;
  search: string;
  onMatchCount: (orgId: string, count: number) => void;
}) {
  const { data, isLoading } = useWebsites(org.id);

  const filtered = search
    ? data?.websites.filter(
        (s: Website) =>
          s.domain.toLowerCase().includes(search.toLowerCase()) ||
          s.url.toLowerCase().includes(search.toLowerCase()),
      )
    : data?.websites;

  useEffect(() => {
    if (!isLoading) {
      onMatchCount(org.id, filtered?.length ?? 0);
    }
  }, [org.id, filtered?.length, isLoading, onMatchCount]);

  // Hide the entire group when searching and no results match
  if (!isLoading && search && filtered?.length === 0) {
    return null;
  }

  return (
    <section
      className="product-section"
      aria-labelledby={`org-${org.id}-heading`}
    >
      <h2 id={`org-${org.id}-heading`} className="section-title">
        {org.name}
      </h2>

      {isLoading && <Skeleton lines={2} />}

      {!isLoading && (!filtered || filtered.length === 0) && (
        <p className="text-muted">No websites yet for this organisation.</p>
      )}

      {!isLoading && filtered && filtered.length > 0 && (
        <div className="website-grid">
          {filtered.map((site: Website) => (
            <WebsiteCard key={site.id} site={site} />
          ))}
        </div>
      )}
    </section>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function WebsitesPage() {
  const { data, isLoading } = useOrgs();
  const [search, setSearch] = useState("");
  const [matchCounts, setMatchCounts] = useState<Record<string, number>>({});

  // Reset match counts when search changes to avoid stale data
  useEffect(() => {
    setMatchCounts({});
  }, [search]);

  const handleMatchCount = useCallback(
    (orgId: string, count: number) => {
      setMatchCounts((prev) => {
        if (prev[orgId] === count) return prev;
        return { ...prev, [orgId]: count };
      });
    },
    [],
  );

  const orgs = data?.orgs ?? [];
  const allGroupsEmpty =
    search &&
    orgs.length > 0 &&
    orgs.every((org) => matchCounts[org.id] === 0);

  return (
    <div className="product-page">
      <div className="page-header">
        <h1 className="page-title">My Websites</h1>
        <Link to="/websites/add" className="btn btn--primary">
          Add website
        </Link>
      </div>
      <p className="page-subtitle">
        All websites you have access to, grouped by organisation.
      </p>

      {!isLoading && orgs.length > 0 && (
        <div className="website-search">
          <input
            type="search"
            className="website-search__input"
            placeholder="Search websites by domain&#x2026;"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search websites"
          />
        </div>
      )}

      {isLoading && <Skeleton lines={2} />}

      {!isLoading && orgs.length === 0 && (
        <p className="text-muted">
          No organisations found.{" "}
          <Link to="/setup/org">Create an organisation</Link> to get started.
        </p>
      )}

      {orgs.map((org) => (
        <OrgWebsiteGroup
          key={org.id}
          org={org}
          search={search}
          onMatchCount={handleMatchCount}
        />
      ))}

      {allGroupsEmpty && (
        <p className="text-muted" role="status" aria-live="polite">
          No websites match &ldquo;{search}&rdquo;.
        </p>
      )}
    </div>
  );
}

